import styled, { css } from 'styled-components';
import { fleetLeading, fleetType } from '@entities/fleet';
import type { ChronumTone } from '@entities/chronum';
import { Eyebrow, NARROW, toneColor } from '../ui';

export const Box = styled.section`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  overflow: hidden;
`;

export const Top = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(6)}`};
  padding: ${({ theme }) => `${theme.spacing(5)} ${theme.spacing(5)} ${theme.spacing(4)}`};

  @container chronum (max-width: ${NARROW}) {
    padding: ${({ theme }) => theme.spacing(4)};
  }
`;

export const Connection = styled.div`
  flex: 1 1 340px;
  min-width: 0;
  max-width: 640px;

  & > h2 {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(2)};
    margin: 0 0 ${({ theme }) => theme.spacing(1.5)};
    font-size: ${fleetType.base};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
  }

  & > p {
    margin: 0;
    font-size: ${fleetType.body};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }

  & code {
    font-family: ${({ theme }) => theme.font.mono};
    font-size: ${fleetType.small};
    overflow-wrap: anywhere;
  }
`;

export const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};

  @container chronum (max-width: ${NARROW}) {
    flex: 1 1 100%;

    & > div {
      flex: 1 1 100%;
    }

    & > button {
      flex: 1 1 100%;
    }
  }
`;

/** Живая область всегда в дереве (пустая — нулевой высоты), иначе итог не прочитается. */
export const Live = styled.div`
  padding: ${({ theme }) => `0 ${theme.spacing(5)}`};

  & > * {
    margin-bottom: ${({ theme }) => theme.spacing(4)};
  }

  & ul {
    margin: ${({ theme }) => `${theme.spacing(1)} 0 0`};
    padding-left: ${({ theme }) => theme.spacing(5)};
  }

  @container chronum (max-width: ${NARROW}) {
    padding: ${({ theme }) => `0 ${theme.spacing(4)}`};
  }
`;

/** Ход загрузки: что грузится, сколько готово, сколько идёт. */
export const Progress = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(3.5)}`};
  border: 1px solid ${({ theme }) => theme.colors.status.info}59;
  border-left: 3px solid ${({ theme }) => theme.colors.status.info};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.status.info}10;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.primary};

  & > .head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(3)}`};
  }

  & .sec {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  & > .note {
    margin: 0;
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }
`;

/** Полоса хода: сколько ручек из скольких готово. */
export const Bar = styled.div<{ $pct: number }>`
  position: relative;
  height: 4px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.border};
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0 auto 0 0;
    width: ${({ $pct }) => `${Math.max(4, Math.min(100, $pct))}%`};
    border-radius: inherit;
    background: ${({ theme }) => theme.colors.status.info};
    transition: width ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};
  }
`;

/** Табло «что приходит из Хронума»: ячейки через волосяную сетку. */
export const Feeds = styled.ul`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1px;
  margin: 0;
  padding: 0;
  list-style: none;
  background: ${({ theme }) => theme.colors.border};
  border-top: 1px solid ${({ theme }) => theme.colors.border};

  @container chronum (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  /* На телефоне табло свёрнуто в строку-итог: до свода не листать полэкрана. */
  @container chronum (max-width: ${NARROW}) {
    &[data-open='false'] {
      display: none;
    }
  }
`;

/** Строка-итог табло — только на узком экране, раскрывает табло. */
export const FeedsToggle = styled.button`
  display: none;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    box-shadow: inset ${({ theme }) => theme.shadow.focus};
  }

  & > .body {
    flex: 1 1 auto;
    min-width: 0;
  }

  & .title {
    display: block;
    font-size: ${fleetType.micro};
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & .counts {
    display: block;
    margin-top: 2px;
    font-size: ${fleetType.body};
    line-height: ${fleetLeading.normal};
  }

  & > svg {
    flex: none;
    color: ${({ theme }) => theme.colors.text.secondary};
    transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};
  }

  &[aria-expanded='true'] > svg {
    transform: rotate(180deg);
  }

  @container chronum (max-width: ${NARROW}) {
    display: flex;
  }
`;

export const Feed = styled.li<{ $tone: ChronumTone }>`
  position: relative;
  min-width: 0;
  padding: ${({ theme }) => `${theme.spacing(3.5)} ${theme.spacing(4)} ${theme.spacing(3)}`};
  background: ${({ theme }) => theme.colors.backgroundElevated};

  /* Полоса итога сверху — как отметка в клетке табеля. Итог ещё и словами. */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: ${({ theme }) => theme.spacing(4)};
    width: 28px;
    height: 3px;
    border-radius: 0 0 ${({ theme }) => `${theme.radius.xs} ${theme.radius.xs}`};
    background: ${({ theme, $tone }) => toneColor(theme, $tone)};
  }

  & > .label {
    display: block;
    font-size: ${fleetType.micro};
    font-weight: 600;
    letter-spacing: 0.05em;
    line-height: ${fleetLeading.tight};
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & > .state {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1.5)};
    margin-top: ${({ theme }) => theme.spacing(1.5)};
    font-size: ${fleetType.body};
    font-weight: 500;
    line-height: ${fleetLeading.tight};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  & > .meta {
    display: block;
    margin-top: ${({ theme }) => theme.spacing(1)};
    font-size: ${fleetType.small};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-variant-numeric: tabular-nums;
    overflow-wrap: anywhere;
  }

  & > .meta + .meta {
    margin-top: 0;
  }

  & > .reason {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  ${({ $tone }) =>
    $tone === 'muted' &&
    css`
      & > .state {
        color: ${({ theme }) => theme.colors.text.secondary};
        font-weight: 400;
      }
    `}

  @container chronum (max-width: ${NARROW}) {
    padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(3)} ${theme.spacing(2.5)}`};

    &::before {
      left: ${({ theme }) => theme.spacing(3)};
    }
  }
`;

export const HintsToggle = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(5)}`};
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font: inherit;
  font-size: ${fleetType.small};
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }

  /* Кольцо фокуса внутрь: снаружи его обрезает рамка панели. */
  &:focus-visible {
    box-shadow: inset ${({ theme }) => theme.shadow.focus};
  }

  & > svg {
    flex: none;
    transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};
  }

  &[aria-expanded='true'] > svg {
    transform: rotate(180deg);
  }

  @container chronum (max-width: ${NARROW}) {
    padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(4)}`};
  }
`;

export const Hints = styled.dl`
  display: grid;
  grid-template-columns: minmax(140px, max-content) minmax(0, 1fr);
  gap: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(4)}`};
  margin: 0;
  padding: ${({ theme }) => `0 ${theme.spacing(5)} ${theme.spacing(4)}`};
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};

  & > dt {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  & > dd {
    margin: 0;
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }

  @container chronum (max-width: ${NARROW}) {
    grid-template-columns: minmax(0, 1fr);
    gap: 2px;
    padding: ${({ theme }) => `0 ${theme.spacing(4)} ${theme.spacing(4)}`};

    & > dd + dt {
      margin-top: ${({ theme }) => theme.spacing(2)};
    }
  }
`;

/** Оборванная прошлая загрузка месяца — без живой области: это состояние, а не событие. */
export const Stopped = styled.div`
  padding: ${({ theme }) => `0 ${theme.spacing(5)} ${theme.spacing(4)}`};

  @container chronum (max-width: ${NARROW}) {
    padding: ${({ theme }) => `0 ${theme.spacing(4)} ${theme.spacing(4)}`};
  }
`;

export const FeedsTitle = styled(Eyebrow)`
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(5)} ${theme.spacing(2.5)}`};

  /* На узком экране заголовок — в строке-итоге (FeedsToggle). */
  @container chronum (max-width: ${NARROW}) {
    display: none;
  }
`;
